import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Modal,
  message,
  Tooltip,
  Dropdown,
  Popconfirm,
  Typography,
  Row,
  Col,
  Statistic,
  Empty
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  MoreOutlined,
  RobotOutlined,
  FileTextOutlined,
  CalendarOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import ActionPlanViewer from './ActionPlanViewer';
import ActionPlanEditor from './ActionPlanEditor';
import { getPatientActionPlans, getActionPlanStats, deleteActionPlan, duplicateActionPlan } from '../services/actionPlanService';

const { Search } = Input;
const { Option } = Select;
const { Text, Title } = Typography;

/**
 * Action Plans Tab Component
 * Displays and manages all action plans for a patient
 */
const ActionPlansTab = ({ patientId: propPatientId }) => {
  const { patientId: urlPatientId } = useParams();
  // Use patientId from props (when used as tab) or URL params (when used as standalone page)
  const patientId = propPatientId || urlPatientId;

  // Debug logging
  console.log('[ActionPlansTab] Patient ID Debug:', {
    propPatientId,
    urlPatientId,
    finalPatientId: patientId
  });

  const [actionPlans, setActionPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [editorVisible, setEditorVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Load action plans and stats
  useEffect(() => {
    if (patientId) {
      loadActionPlans();
      loadStats();
    }
  }, [patientId, searchQuery, statusFilter, pagination.current, pagination.pageSize]);

  const loadActionPlans = async () => {
    try {
      setLoading(true);
      const params = {
        limit: pagination.pageSize,
        skip: (pagination.current - 1) * pagination.pageSize,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      const response = await getPatientActionPlans(patientId, params);
      
      if (response.success) {
        setActionPlans(response.data.actionPlans);
        setPagination(prev => ({
          ...prev,
          total: response.data.count
        }));
      }
    } catch (error) {
      console.error('Error loading action plans:', error);
      message.error('Failed to load action plans');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await getActionPlanStats(patientId);
      if (response.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleView = (plan) => {
    setSelectedPlan(plan);
    setViewerVisible(true);
  };

  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    setEditorVisible(true);
  };

  const handleDuplicate = async (plan) => {
    try {
      const response = await duplicateActionPlan(plan._id, {
        title: `${plan.title} (Copy)`
      });
      
      if (response.success) {
        message.success('Action plan duplicated successfully');
        loadActionPlans();
        loadStats();
      }
    } catch (error) {
      console.error('Error duplicating action plan:', error);
      message.error('Failed to duplicate action plan');
    }
  };

  const handleNewActionPlan = () => {
    setSelectedPlan(null); // null indicates creating a new plan
    setEditorVisible(true);
  };

  const handleDelete = async (plan) => {
    try {
      console.log('[ActionPlansTab] Attempting to delete action plan:', plan._id);

      const response = await deleteActionPlan(plan._id, {
        reason: 'Deleted by user'
      });

      console.log('[ActionPlansTab] Delete response:', response);

      if (response.success) {
        message.success('Action plan deleted successfully');
        loadActionPlans();
        loadStats();
      } else {
        message.error(response.message || 'Failed to delete action plan');
      }
    } catch (error) {
      console.error('Error deleting action plan:', error);

      let errorMessage = 'Failed to delete action plan';

      if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to delete action plans. Only MDs and Admins can delete action plans.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Action plan not found or already deleted';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'orange',
      active: 'green',
      archived: 'blue',
      cancelled: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (planType) => {
    const icons = {
      ai_generated: <RobotOutlined />,
      manual: <FileTextOutlined />,
      hybrid: <EditOutlined />
    };
    return icons[planType] || <FileTextOutlined />;
  };

  const getActionMenu = (plan) => ({
    items: [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: 'View Plan',
        onClick: () => handleView(plan)
      },
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Edit Plan',
        onClick: () => handleEdit(plan)
      },
      {
        key: 'duplicate',
        icon: <CopyOutlined />,
        label: 'Duplicate',
        onClick: () => handleDuplicate(plan)
      },
      {
        type: 'divider'
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Delete',
        danger: true,
        onClick: () => {
          Modal.confirm({
            title: 'Delete Action Plan',
            content: 'Are you sure you want to delete this action plan? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            onOk: () => handleDelete(plan)
          });
        }
      }
    ]
  });

  const columns = [
    {
      title: 'Plan Title',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => (
        <Space>
          <Tooltip title={`Plan Type: ${record.planType?.replace('_', ' ')}`}>
            {getStatusIcon(record.planType)}
          </Tooltip>
          <Text strong>{title}</Text>
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Language',
      key: 'language',
      render: (_, record) => {
        // Check multiple possible locations for language info
        const uiLanguage = record.aiGenerationData?.generationParameters?.uiLanguage;
        const language = record.language;

        const isVietnamese = uiLanguage === 'vietnamese' || language === 'vietnamese';
        return <Tag color="blue">{isVietnamese ? 'VI' : 'EN'}</Tag>;
      }
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy) => (
        <Space>
          <UserOutlined />
          <Text>{createdBy?.name || 'Unknown'}</Text>
        </Space>
      )
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <Space>
          <CalendarOutlined />
          <Text>{new Date(date).toLocaleDateString()}</Text>
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            title="View Plan"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit Plan"
          />
          <Popconfirm
            title="Delete Action Plan"
            description="Are you sure you want to delete this action plan? This action cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            cancelText="Cancel"
            okType="danger"
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              title="Delete Plan"
            />
          </Popconfirm>
          <Dropdown menu={getActionMenu(record)} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      )
    }
  ];

  if (!patientId) {
    console.error('[ActionPlansTab] No patient ID available:', {
      propPatientId,
      urlPatientId,
      finalPatientId: patientId
    });

    return (
      <Card>
        <Empty
          description={
            <div>
              <Text type="secondary">Patient ID not found</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Debug: propPatientId={propPatientId || 'null'}, urlPatientId={urlPatientId || 'null'}
              </Text>
            </div>
          }
        />
      </Card>
    );
  }

  return (
    <div>
      {/* Statistics Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Plans"
                value={stats.total}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Active Plans"
                value={stats.active}
                prefix={<RobotOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Draft Plans"
                value={stats.draft}
                prefix={<EditOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Archived Plans"
                value={stats.archived}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Main Action Plans Table */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <Title level={4} style={{ margin: 0 }}>Action Plans</Title>
          </Space>
        }
        extra={
          <Space>
            <Search
              placeholder="Search action plans..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 250 }}
            />
            <Select
              value={statusFilter}
              onChange={handleStatusFilter}
              style={{ width: 120 }}
            >
              <Option value="all">All Status</Option>
              <Option value="draft">Draft</Option>
              <Option value="active">Active</Option>
              <Option value="archived">Archived</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleNewActionPlan}
            >
              Add New Action Plan
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={actionPlans}
          loading={loading}
          rowKey="_id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} plans`
          }}
          onChange={(paginationConfig) => {
            setPagination({
              current: paginationConfig.current,
              pageSize: paginationConfig.pageSize,
              total: paginationConfig.total
            });
          }}
        />
      </Card>

      {/* Action Plan Viewer Modal */}
      <ActionPlanViewer
        visible={viewerVisible}
        actionPlan={selectedPlan}
        onClose={() => {
          setViewerVisible(false);
          setSelectedPlan(null);
        }}
        onEdit={() => {
          setViewerVisible(false);
          setEditorVisible(true);
        }}
      />

      {/* Action Plan Editor Modal */}
      <ActionPlanEditor
        visible={editorVisible}
        actionPlan={selectedPlan}
        patientId={patientId}
        onClose={() => {
          setEditorVisible(false);
          setSelectedPlan(null);
        }}
        onSave={() => {
          setEditorVisible(false);
          setSelectedPlan(null);
          loadActionPlans();
          loadStats();
        }}
      />
    </div>
  );
};

export default ActionPlansTab;
